import { Loader2, Lock, X } from "lucide-react";
import { toast } from "sonner";

import { StatusPill } from "@/components/status-pill";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  type AdminAccountPermissions,
  type AdminCapability,
  type AdminGrantState,
  apiErrorMessage,
  type CustomerPermissionUpdate,
  useSetCustomerPermission,
} from "@/lib/api";
import { cn } from "@/lib/utils";

import { usePendingIdSet } from "../use-pending-id-set";

interface CustomerPermissionGridProps {
  /** Same payload shape the admin grid renders, scoped to the caller's account. */
  data: AdminAccountPermissions;
  /** The signed-in customer's own contact id — their admin cell is locked. */
  selfContactId?: string | null;
}

type GrantEffect = NonNullable<CustomerPermissionUpdate["effect"]>;

const APPROVAL_OPTIONS: { value: GrantEffect; label: string }[] = [
  { value: "allow", label: "Allow" },
  { value: "allow_with_approval", label: "Require approval" },
  { value: "deny", label: "Deny" },
];

// The capability a customer admin manages for their colleagues but may not
// change for themselves — the backend 400s a self-edit of it.
const isAccountAdminCap = (cap: AdminCapability) =>
  cap.record_type === "account" && cap.action === "admin";

const cellKey = (contactId: string, cap: AdminCapability) =>
  `${contactId}:${cap.record_type}:${cap.action}`;

/**
 * Customer-facing permission grid. Unlike the admin grid this account manages
 * only its OWN colleagues: the account-default row is read-only (it's the
 * J&J-set ceiling), and only capabilities flagged `customer_grantable` expose a
 * control. A customer admin may overwrite a J&J-authored grant (the backend
 * flips it to customer-authored) but may not change their own admin access.
 */
export function CustomerPermissionGrid({ data, selfContactId }: CustomerPermissionGridProps) {
  const setPermission = useSetCustomerPermission();
  const pending = usePendingIdSet();
  const failed = usePendingIdSet();

  const { capabilities, account_defaults, contacts } = data;

  const applyUpdate = async (key: string, update: CustomerPermissionUpdate) => {
    pending.add(key);
    failed.remove(key);
    try {
      await setPermission.mutateAsync(update);
    } catch (err) {
      failed.add(key);
      toast.error(apiErrorMessage(err, "Couldn't update this permission. Please try again."));
    } finally {
      pending.remove(key);
    }
  };

  const handleSetEffect = (contactId: string, cap: AdminCapability, effect: GrantEffect) => {
    void applyUpdate(cellKey(contactId, cap), {
      contact_id: contactId,
      record_type: cap.record_type,
      action: cap.action,
      effect,
    });
  };

  const handleClear = (contactId: string, cap: AdminCapability) => {
    void applyUpdate(cellKey(contactId, cap), {
      contact_id: contactId,
      record_type: cap.record_type,
      action: cap.action,
      effect: null,
    });
  };

  const columnCount = capabilities.length + 1;

  return (
    <div className="bg-card border-border overflow-hidden rounded-lg border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-warm-gray-01 border-border border-b">
            <tr>
              <th className="text-muted px-4 py-3 text-left text-[10px] font-medium tracking-[0.16em] uppercase">
                Person
              </th>
              {capabilities.map((cap) => (
                <th
                  key={`${cap.record_type}:${cap.action}`}
                  className="text-muted px-4 py-3 text-center text-[10px] font-medium tracking-[0.16em] uppercase"
                >
                  <div className="flex flex-col items-center">
                    <span className="flex items-center gap-1">
                      {cap.label}
                      {!cap.customer_grantable && (
                        <span title="Managed by Johnson & Johnson" className="inline-flex">
                          <Lock
                            className="text-muted h-3 w-3"
                            aria-label="Managed by Johnson & Johnson"
                          />
                        </span>
                      )}
                    </span>
                    <span className="text-muted font-mono text-[10px] tracking-normal normal-case">
                      {cap.record_type}/{cap.action}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Account ceiling — read-only: it's set by J&J, not the customer. */}
            <tr className="border-border bg-warm-gray-01/60 border-b">
              <td
                colSpan={columnCount}
                className="text-muted px-4 py-2 text-[10px] font-medium tracking-[0.16em] uppercase"
              >
                Account default (set by J&amp;J)
              </td>
            </tr>
            <tr className="border-border border-b">
              <td className="px-4 py-3 font-medium">All colleagues</td>
              {capabilities.map((cap, index) => {
                const grant = account_defaults[index];
                return (
                  <td
                    key={`${cap.record_type}:${cap.action}`}
                    className="px-4 py-3 text-center align-top"
                  >
                    {grant ? <ReadOnlyEffect effective={grant.effective} /> : null}
                  </td>
                );
              })}
            </tr>

            <tr className="border-border bg-warm-gray-01/60 border-b">
              <td
                colSpan={columnCount}
                className="text-muted px-4 py-2 text-[10px] font-medium tracking-[0.16em] uppercase"
              >
                Colleagues
              </td>
            </tr>
            {contacts.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="text-muted px-4 py-8 text-center text-sm">
                  No colleagues yet. Invite someone to get started.
                </td>
              </tr>
            ) : (
              contacts.map((contact) => (
                <tr
                  key={contact.contact_id}
                  className={cn(
                    "border-border border-b last:border-0",
                    !contact.active && "opacity-60",
                  )}
                >
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-col">
                      <span className="flex flex-wrap items-center gap-2 font-medium">
                        {contact.full_name}
                        {contact.role && <StatusPill>{contact.role}</StatusPill>}
                        {!contact.active && <StatusPill>inactive</StatusPill>}
                      </span>
                      {contact.email && <span className="text-muted text-xs">{contact.email}</span>}
                    </div>
                  </td>
                  {capabilities.map((cap, index) => {
                    const grant = contact.grants[index];
                    if (!grant) {
                      return <td key={`${cap.record_type}:${cap.action}`} className="px-4 py-3" />;
                    }
                    const key = cellKey(contact.contact_id, cap);
                    // A customer admin can edit any grantable cell — including
                    // J&J-authored ones — except their own admin access, which
                    // stays locked (the backend rejects a self-edit of it).
                    const isOwnAdminCell =
                      !!selfContactId &&
                      contact.contact_id === selfContactId &&
                      isAccountAdminCap(cap);
                    const isEditable = cap.customer_grantable && !isOwnAdminCell;
                    const lockTitle = isOwnAdminCell
                      ? "You can't change your own admin access"
                      : !cap.customer_grantable
                        ? "Managed by Johnson & Johnson"
                        : undefined;
                    return (
                      <td key={`${cap.record_type}:${cap.action}`} className="px-4 py-3">
                        {isEditable ? (
                          <EditableCell
                            capability={cap}
                            grant={grant}
                            scopeName={contact.full_name}
                            disabled={!contact.active}
                            isPending={pending.ids.has(key)}
                            isFailed={failed.ids.has(key)}
                            onSetEffect={(effect) =>
                              handleSetEffect(contact.contact_id, cap, effect)
                            }
                            onClear={() => handleClear(contact.contact_id, cap)}
                          />
                        ) : (
                          <div className="flex justify-center">
                            <ReadOnlyEffect effective={grant.effective} lockTitle={lockTitle} />
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReadOnlyEffect({
  effective,
  lockTitle,
}: {
  effective: AdminGrantState["effective"];
  /** When set, shows a lock icon with this tooltip explaining why it's locked. */
  lockTitle?: string;
}) {
  const tone = effective === "deny" ? "neutral" : effective === "allow" ? "green" : "orange";
  const label =
    effective === "allow" ? "Allowed" : effective === "deny" ? "Denied" : "Needs approval";
  return (
    <StatusPill tone={tone} title={lockTitle}>
      {lockTitle && <Lock className="h-2.5 w-2.5" aria-label={lockTitle} />}
      {label}
    </StatusPill>
  );
}

interface EditableCellProps {
  capability: AdminCapability;
  grant: AdminGrantState;
  scopeName: string;
  disabled: boolean;
  isPending: boolean;
  isFailed: boolean;
  onSetEffect: (effect: GrantEffect) => void;
  onClear: () => void;
}

function EditableCell({
  capability,
  grant,
  scopeName,
  disabled,
  isPending,
  isFailed,
  onSetEffect,
  onClear,
}: EditableCellProps) {
  const isExplicit = grant.effect != null;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex items-center gap-1.5">
        {isPending && <Loader2 className="text-muted h-3 w-3 animate-spin" />}
        {capability.approvable ? (
          <Select
            value={grant.effective}
            disabled={isPending || disabled}
            onValueChange={(value) => onSetEffect(value as GrantEffect)}
          >
            <SelectTrigger
              size="sm"
              className={cn(
                "w-[132px] px-2 text-xs",
                grant.effective === "allow_with_approval" && "text-jj-orange-05",
              )}
              aria-label={`${capability.label} for ${scopeName}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {APPROVAL_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value} className="text-xs">
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Switch
            size="sm"
            checked={grant.effective === "allow"}
            disabled={isPending || disabled}
            onCheckedChange={(enabled) => onSetEffect(enabled ? "allow" : "deny")}
            aria-label={`${capability.label} for ${scopeName}`}
          />
        )}
      </div>
      {isExplicit ? (
        <button
          type="button"
          onClick={onClear}
          disabled={isPending || disabled}
          className="text-accent hover:text-accent/80 flex items-center gap-0.5 text-xs disabled:opacity-50"
          aria-label={`Clear ${capability.label} override for ${scopeName}`}
        >
          custom
          <X className="h-3 w-3" />
        </button>
      ) : (
        <span className="text-muted text-xs">default</span>
      )}
      {isFailed && <span className="text-destructive text-xs">failed</span>}
    </div>
  );
}
